import { Component, computed, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { Role } from 'src/app/models/user/role.model';
import { MBRDocumentsService } from 'src/app/services/mbr-documents.service';
import { MatDrawer, MatSidenavModule } from '@angular/material/sidenav';
import { debounceTime, of, Subscription, switchMap, tap } from 'rxjs';
import { UsersService } from 'src/app/services/users.service';
import { ColDef, GridApi, GridReadyEvent, ValueGetterParams } from 'ag-grid-community';
import { GridDocumentComponent } from '../../components/grid-document/grid-document.component';
import { TextTooltipRenderer } from '../../components/text-tooltip-cell-renderer/text-tooltip-cell-renderer.component';
import { baseColDef } from '../../utils';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { RightSidebarContainerComponent } from '../../components/right-sidebar-container/right-sidebar-container.component';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatTabsModule } from '@angular/material/tabs';
import { AgGridWrapperComponent } from '../../components/ag-grid-wrapper/ag-grid-wrapper.component';
import { ProviderDocumentsComponent } from '../../components/provider-documents/provider-documents.component';
import { RowDetailsDataComponent } from '../../components/row-detail-data/row-details-data.component';
import { SearchSortComponent } from '../../components/search-sort/search-sort.component';
import { ProviderInvoicesComponent } from '../providers-list/provider-invoices/provider-invoices.component';
import { MatButtonModule } from '@angular/material/button';

@Component({
    selector: 'app-product-mbr-list',
    templateUrl: './product-mbr-list.component.html',
    styleUrls: ['./product-mbr-list.component.scss'],
    standalone: true,
    imports: [
      MatSidenavModule,
      MatFormFieldModule,
      ReactiveFormsModule,
      MatTabsModule,
      SearchSortComponent,
      RouterModule,
      MatIconModule,
      AgGridWrapperComponent,
      MatPaginatorModule,
      MatButtonModule
    ]
})
export class ProductMbrListComponent implements OnInit, OnDestroy {
  @ViewChild('drawer') drawer: MatDrawer;
  gridAPI: GridApi;

  isLoading: boolean;

  pageSettings: {
    defaultPageSize: number,
    pageSizeOptions: number[],
  } = {
    defaultPageSize: 20,
    pageSizeOptions: [20, 50 ,100]
  }
  currentPage: number = 0;
  pageSize: number = this.pageSettings.defaultPageSize;
  searchControl: FormControl = new FormControl('');
  currentSortField = {
    sortValue: 'organization',
  };
  currentIsAscending = true;

  sortForm: FormGroup = new FormGroup({
    sortField: new FormControl({
      sortValue: this.currentSortField.sortValue,
    }),
    isAscending: new FormControl(this.currentIsAscending),
  });

  sortOptions = [
    { label: 'Lender', sortValue: 'organization' },
    { label: 'Product', sortValue: 'product' },
  ];

  totalCount: number = 0;
  searchKey: string = '';
  readonly userRole = toSignal(this.userService.userRole$, { initialValue: undefined });
  readonly isLender = computed(() => this.userRole() === Role.Lender);
  readonly isAdmin = computed(() => this.userRole() === Role.Admin);
  readonly isProvider= computed(() => this.userRole() === Role.Provider);
  readonly isSystemUser= computed(() => this.userRole() === Role.SystemUser);
  readonly isConsultant= computed(() => this.userRole() === Role.Consultant);
  readonly isProviderReadOnly = computed(() => this.userRole() === Role.ProviderReadOnly);
  readonly isLenderReadOnly = computed(() => this.userRole() === Role.LenderReadOnly);

  public columnDefs: ColDef[] = [
    {
      ...baseColDef,
      field: 'product.name',
      cellRenderer: TextTooltipRenderer,
      cellRendererParams: !this.isProvider() && !this.isConsultant() && !this.isProviderReadOnly() && !this.isLenderReadOnly() ? {
        onClick: this.onView.bind(this)
      } : undefined,
      headerName: 'Product',
      flex: 3
    },
    { ...baseColDef, field: 'organization.name', cellRenderer: TextTooltipRenderer, headerName: 'Lender', flex: 3 },
    {
      ...baseColDef,
      field: 'mbrDocument.name',
      headerName: 'MBR Document',
      flex:3,
      cellRenderer: GridDocumentComponent,
      valueGetter: (params: ValueGetterParams) => {
        return params.data.mbrDocument ? [params.data.mbrDocument] : [];
      }
    },
    { ...baseColDef, field: 'mbrDocument.version', cellRenderer: TextTooltipRenderer, headerName: 'Version', flex:3 },
  ];

  subscriptions: Subscription[] = [];
  public rowData!: any[];

  constructor(
    private mbrService: MBRDocumentsService,
    private userService: UsersService,
    private router: Router,
  ) {

  }

  ngOnInit() {
    const searchSubscription =this.searchControl.valueChanges
    .pipe(
      debounceTime(300),
      tap(() => {
        this.currentPage = 0;
        this.reload();
      }),
    )
    .subscribe();
    this.subscriptions.push(searchSubscription);
  }

  onRequestMBR(){
    if(this.isAdmin() || this.isSystemUser()){
      this.router.navigate(['/admin/mbr-request/create']);
    }
    else{
      this.router.navigate(['/lender/mbr-request/create']);
    }
  }

  onView(data: any) {
    if(this.isAdmin() || this.isSystemUser()){
      this.router.navigate([`/admin/product-mbr/${data.product.id}/${data.organization.id}`]);
    }
    else if(this.isProviderReadOnly() || this.isProvider() || this.isConsultant()){
      this.router.navigate([`/provider/product-mbr/${data.product.id}/${data.organization.id}`]);
    }
    else{
      this.router.navigate([`/lender/product-mbr/${data.product.id}/${data.organization.id}`]);
    }
  }

  close() {
    this.drawer.close();
  }

  canCreate() {
    return !this.isProvider() && !this.isConsultant() && !this.isProviderReadOnly() && !this.isLenderReadOnly();
  }

  private reload() {
    this.isLoading = true;
    this.searchKey = this.searchControl.value?.toLowerCase() || '';
    this.gridAPI.showLoadingOverlay();
    const subscription = this.mbrService
    .get({
      sortColumn: this.currentSortField.sortValue,
      sortOrder: this.currentIsAscending ? 'asc' : 'desc',
      page: this.currentPage + 1,
      pageSize: this.pageSize,
      searchKey: this.searchKey
    }).pipe(
      switchMap((response: any) => {
        if (!response.isError) {
          return of(response.data);
        } else {
          return of([]);
        }
      })).subscribe(data => {
        this.gridAPI.hideOverlay();
        this.totalCount = data.totalCount;
        this.rowData = data.items;
        this.isLoading = false;
        this.drawer.close();
      }
    );
    this.subscriptions.push(subscription);
  }

  handlePageEvent(event: PageEvent){
    this.currentPage = event.pageIndex;
    this.pageSize = event.pageSize;
    this.reload();
  }

  setSortField() {
    const { sortField, isAscending } = this.sortForm.value;
    this.currentIsAscending = isAscending;
    this.currentSortField = sortField;

    this.reload();
  }

  resetSortField() {
    this.sortForm.setValue({
      sortField: this.currentSortField,
      isAscending: this.currentIsAscending
    })
  }

  //-------------GRID FUNCTIONS

  onGridReady(params: GridReadyEvent) {
    this.gridAPI = params.api;
    this.reload();
  }

  ngOnDestroy(): void {
    this.subscriptions?.forEach(s => s.unsubscribe());
  }
}
